// Topbar. On desktop: breadcrumbs + view toggle + upload. On mobile:
// two modes — default (hamburger, back-up, collapsed breadcrumb,
// overflow menu) and selection (close + count). Action icons in
// selection mode live in the SelectionActionBar (fixed bottom); this
// bar only shows the count and the dismiss button so the two bars
// don't duplicate controls.

import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowUp,
  ChevronDown,
  Menu,
  MoreVertical,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useNavigation, type SortCol, type SortDir } from "../../stores/navigation";
import { useUi } from "../../stores/ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { BottomSheet } from "../ui/bottom-sheet";
import { BreadcrumbSheet } from "../browser/BreadcrumbSheet";

export function Topbar() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileTopbar /> : <DesktopTopbar />;
}

function DesktopTopbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultId } = useParams();
  const view = useNavigation((s) => s.view);
  const setView = useNavigation((s) => s.setView);
  const segments = useSegments(location.pathname);

  const onBreadcrumb = (index: number) => {
    if (!vaultId) return;
    const trimmed = segments.slice(0, index + 1).join("/");
    navigate(`/v/${vaultId}/~/${trimmed}`);
  };

  const onNewFolder = () => {
    window.dispatchEvent(new CustomEvent("vaultwisp:new-folder"));
  };
  const onUpload = () => {
    window.dispatchEvent(new CustomEvent("vaultwisp:open-upload-picker"));
  };

  return (
    <div className="flex items-center gap-4 border-b border-border px-5 py-3 text-sm">
      <div className="flex flex-1 items-center gap-2 text-text-soft">
        <button
          onClick={() => vaultId && navigate(`/v/${vaultId}/~/`)}
          className="hover:text-text"
        >
          root
        </button>
        {segments.map((seg, i) => (
          <span key={i} className="flex items-center gap-2">
            <span className="text-text-dim">/</span>
            <button
              onClick={() => onBreadcrumb(i)}
              className={cn(
                "hover:text-text",
                i === segments.length - 1 && "font-medium text-text",
              )}
            >
              {decodeURIComponent(seg)}
            </button>
          </span>
        ))}
      </div>

      <div className="flex overflow-hidden rounded-md bg-bg-sidebar p-0.5 text-xs">
        <button
          onClick={() => setView("list")}
          className={cn(
            "rounded px-2 py-1 text-text-dim",
            view === "list" && "bg-border text-text",
          )}
        >
          ☰ List
        </button>
        <button
          onClick={() => setView("grid")}
          className={cn(
            "rounded px-2 py-1 text-text-dim",
            view === "grid" && "bg-border text-text",
          )}
        >
          ⊞ Grid
        </button>
      </div>

      <button
        onClick={onNewFolder}
        className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-text-soft hover:bg-accent-bg hover:text-text"
      >
        ＋ New folder
      </button>
      <button
        onClick={onUpload}
        className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white"
      >
        ＋ Upload
      </button>
    </div>
  );
}

function MobileTopbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultId } = useParams();
  const setDrawerOpen = useUi((s) => s.setDrawerOpen);
  const selectionActive = useNavigation((s) => s.selectionActive);
  const selectionCount = useNavigation((s) => s.selection.size);
  const clearSelection = useNavigation((s) => s.clearSelection);
  const segments = useSegments(location.pathname);

  const [crumbOpen, setCrumbOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const onJump = (index: number) => {
    if (!vaultId) return;
    if (index < 0) {
      navigate(`/v/${vaultId}/~/`);
      return;
    }
    const trimmed = segments.slice(0, index + 1).join("/");
    navigate(`/v/${vaultId}/~/${trimmed}`);
  };

  const onUp = () => onJump(segments.length - 2);
  const atRoot = segments.length === 0;
  const currentLabel = atRoot ? "root" : decodeURIComponent(segments[segments.length - 1] ?? "");

  if (selectionActive) {
    return (
      <div className="flex items-center gap-2 border-b border-border px-2 py-2 text-sm">
        <IconBtn onClick={clearSelection} aria-label="Exit selection">
          <X className="h-5 w-5" />
        </IconBtn>
        <span className="min-w-0 flex-1 truncate font-semibold">
          {selectionCount} selected
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1 border-b border-border px-2 py-2 text-sm">
        <IconBtn onClick={() => setDrawerOpen(true)} aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </IconBtn>
        <IconBtn onClick={onUp} disabled={atRoot} aria-label="Up one folder">
          <ArrowUp className="h-5 w-5" />
        </IconBtn>
        <button
          type="button"
          onClick={() => setCrumbOpen(true)}
          className="flex min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-2 text-left text-text-soft hover:bg-accent-bg hover:text-text"
        >
          <span className="truncate font-medium text-text">{currentLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-dim" />
        </button>
        <IconBtn
          onClick={() => setMenuOpen(true)}
          aria-label="More options"
        >
          <MoreVertical className="h-5 w-5" />
        </IconBtn>
      </div>

      <BreadcrumbSheet
        open={crumbOpen}
        onOpenChange={setCrumbOpen}
        segments={segments}
        onJump={onJump}
      />

      <TopbarMenuSheet open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );
}

function TopbarMenuSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (n: boolean) => void;
}) {
  const view = useNavigation((s) => s.view);
  const setView = useNavigation((s) => s.setView);
  const sort = useNavigation((s) => s.sort);
  const setSort = useNavigation((s) => s.setSort);

  const pickSort = (col: SortCol) => {
    const dir: SortDir =
      sort.col === col ? (sort.dir === "asc" ? "desc" : "asc") : "asc";
    setSort({ col, dir });
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="View options">
      <div className="flex flex-col gap-4">
        <section>
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
            Layout
          </div>
          <div className="flex gap-2">
            <SegBtn active={view === "list"} onClick={() => setView("list")}>
              ☰ List
            </SegBtn>
            <SegBtn active={view === "grid"} onClick={() => setView("grid")}>
              ⊞ Grid
            </SegBtn>
          </div>
        </section>
        <section>
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
            Sort by
          </div>
          <div className="flex flex-wrap gap-2">
            {(["name", "size", "modified", "type"] as SortCol[]).map((col) => (
              <SegBtn
                key={col}
                active={sort.col === col}
                onClick={() => pickSort(col)}
              >
                {col}
                {sort.col === col && (
                  <span className="ml-1 text-text-dim">
                    {sort.dir === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </SegBtn>
            ))}
          </div>
        </section>
      </div>
    </BottomSheet>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md border border-border px-3 py-2 text-sm text-text-soft",
        active && "border-accent bg-accent-bg text-text",
      )}
    >
      {children}
    </button>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  "aria-label": ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  "aria-label": string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "grid h-10 w-10 place-items-center rounded-md text-text-soft hover:bg-accent-bg hover:text-text",
        disabled && "opacity-30 hover:bg-transparent hover:text-text-soft",
      )}
    >
      {children}
    </button>
  );
}

function useSegments(pathname: string): string[] {
  return useMemo(() => {
    const match = pathname.match(/^\/v\/[^/]+\/~\/(.*)$/);
    return match && match[1] ? match[1].split("/").filter(Boolean) : [];
  }, [pathname]);
}
